import type { UIComponent } from "@gik/core/types/UI";
import { useBemCN } from "@gik/core/utils/bemBlock";
import { SvgIcon } from "@gik/ui-legacy/SvgIcon";
import SwitchHorizontalIcon from "@heroicons/react/outline/SwitchHorizontalIcon";
import React from "react";
import type { Color } from "react-color";
import { ColorPicker } from "./ColorPicker";
import { SwatchHexPill } from "./components/SwatchHexPill";

export const defaultGradient = [
	{ color: "#7AFFE7", position: 0 },
	{ color: "#003F88", position: 100 },
];

export type GradientValue = {
	color: Color;
	position: number;
};

export type GradientPickerProps = {
	onChange?: (colors: GradientValue[]) => void;
	value?: GradientValue[];
	initialValue?: GradientValue[];
} & UIComponent;

export function parseColorsToGradientValues(colors: GradientValue[]) {
	if (!colors) return undefined;
	return colors
		.map((value) => {
			return `${value.color} ${value.position}%`;
		})
		.join(",");
}

export function GradientPicker({
	onChange,
	initialValue,
	value,
	className,
	...otherProps
}: GradientPickerProps) {
	const bem = useBemCN("gradient-picker");
	const controlled = !!value;
	const [_value, _setValue] = React.useState<GradientValue[]>(() => {
		if (!initialValue) return defaultGradient;
		return initialValue.map((v, i) => ({
			...v,
			color:
				v.color ||
				defaultGradient[i]?.color ||
				(i === 0 ? "#7AFFE7" : "#003F88"),
		}));
	});
	const [selectedStep, setSelectedStep] = React.useState<number>(0);

	const realValue = React.useMemo(() => {
		return (controlled ? value : _value) || [];
	}, [controlled, value, _value]);

	const commit = React.useCallback(
		(next: GradientValue[]) => {
			onChange?.(next);
			if (!controlled) _setValue(next);
		},
		[controlled, onChange],
	);

	const setStepColor = React.useCallback(
		(index: number, color: Color) => {
			const next = realValue.concat([]);
			if (!next[index]) return;
			next[index] = { ...next[index], color };
			commit(next);
		},
		[realValue, commit],
	);

	const handleColorChange = React.useCallback(
		(color: Color) => setStepColor(selectedStep, color),
		[selectedStep, setStepColor],
	);

	const handleSwap = React.useCallback(() => {
		// Swap the colors while keeping the stop positions in place.
		const next = realValue.map((v, i) => ({
			...v,
			color: realValue[realValue.length - 1 - i]?.color,
		}));
		commit(next);
	}, [realValue, commit]);

	const handlePickColor = React.useCallback((index: number) => {
		setSelectedStep(index);
	}, []);

	const gradient = realValue ? parseColorsToGradientValues(realValue) : "";

	return (
		<div {...bem(null, null, className)} {...otherProps}>
			<div {...bem("editor")}>
				<div
					{...bem("preview")}
					style={{
						background: `linear-gradient(90deg,${gradient} )`,
					}}
				>
					{realValue.map((stepValue, i) => {
						if (!stepValue) return null;
						const stopClass = bem("stop", [
							{ start: i === 0 },
							{ end: i === realValue.length - 1 },
						]).className;
						return (
							<SwatchHexPill
								key={i}
								className={stopClass}
								color={stepValue?.color}
								active={selectedStep === i}
								onSwatchClick={() => handlePickColor(i)}
								onChange={(color) => setStepColor(i, color)}
							/>
						);
					})}
					<button
						type="button"
						{...bem("swap")}
						onClick={handleSwap}
						aria-label="Swap gradient colors"
					>
						<SvgIcon Icon={SwitchHorizontalIcon} />
					</button>
				</div>
			</div>

			<ColorPicker
				value={realValue[selectedStep]?.color}
				onChange={handleColorChange}
				renderHeader={null}
			/>
		</div>
	);
}
